import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection, useInView } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const AboutSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const [ref, isInView] = useInView({ threshold: 0.2 });
  if (!data?.bio) return null;
  
  const focalPoint = data?.profile_focal_point || { x: 50, y: 50 };

  return (
    <section id="about" ref={ref} className={cn("py-32 px-4 overflow-hidden relative", t.bg)}>
      {/* Background accent */}
      <div 
        className="absolute top-0 right-0 w-1/2 h-full opacity-5"
        style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, transparent 70%)` }}
      />
      
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            Get to Know
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'About'}
          </h2>
        </AnimatedSection>
        
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {data.profile_image && data.image_style === 'cutout' && (
            <AnimatedSection animation="slideRight" delay={200}>
              <img
                src={data.profile_image}
                alt="About"
                className={cn(
                  "w-full max-h-[560px] object-contain drop-shadow-2xl transition-all duration-1000",
                  isInView ? "scale-100" : "scale-95"
                )}
                data-testid="about-cutout-image"
              />
            </AnimatedSection>
          )}
          {data.profile_image && data.image_style !== 'cutout' && (
            <AnimatedSection animation="slideRight" delay={200}>
              <div className="relative">
                {/* Decorative frame */}
                <div 
                  className="absolute -inset-4 rounded-3xl opacity-20"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, transparent)` }}
                />
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={data.profile_image} 
                    alt="Profile" 
                    className={cn(
                      "w-full h-full object-cover transition-all duration-1000",
                      isInView ? "scale-100" : "scale-110"
                    )}
                    style={{ objectPosition: `${focalPoint.x}% ${focalPoint.y}%` }}
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>
                {/* Floating accent */}
                <div 
                  className="absolute -bottom-6 -right-6 w-32 h-32 rounded-2xl -z-10"
                  style={{ backgroundColor: primaryColor, opacity: 0.2 }}
                />
              </div>
            </AnimatedSection>
          )}
          <AnimatedSection 
            animation="slideLeft" 
            delay={400}
            className={cn(!data.profile_image && "md:col-span-2 text-center max-w-3xl mx-auto")}
          >
            <p className={cn("text-lg md:text-xl leading-relaxed whitespace-pre-line", t.textMuted)}>
              {data.bio}
            </p>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};


